import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';

import { ProyectoService } from '../services/proyecto.service';
import { SocietyService } from '../services/society.service';
import { TypeService } from '../services/type.service';
import { SystemService } from '../services/systems.service';
import { AuthService } from '../auth/auth.service';
import { LoggerService } from '../services/logger.service';
import { GlobalModalService } from '../shared/global-modal/global-modal.service';

import { ProjectListItem, ProjectListQueryParams } from '../models/project-list.model';
import { PagedResponse, DEFAULT_PAGE_SIZE } from '../models/pagination.model';
import { Society } from '../models/society.model';
import { Type } from '../models/type.model';
import { System } from '../models/system.model';

/** Claves de filtros plegables (no incluye búsqueda ni sort). */
type FilterKey = 'societyId' | 'typeId' | 'systemId';

/** Chip activo: lo que se muestra y la clave de URL que representa. */
interface ActiveFilterChip {
  key: FilterKey;
  label: string;
}

/**
 * Listado paginado de proyectos.
 *
 * Arquitectura:
 *  - URL (queryParams) como ÚNICA fuente de verdad del estado de listado.
 *  - Pipeline RxJS con switchMap que cancela peticiones obsoletas.
 *  - searchSubject$ con debounce 300ms para la búsqueda libre.
 *  - Boot-sequence solo en la PRIMERA carga; recargas posteriores con
 *    isReloading (loading sutil: opacidad + paginador disabled).
 *  - DTO ligero (ProjectListItem): el listado nunca trae estimation,
 *    taskexecutions, systems ni technologies; sólo lo que renderiza.
 *  - Filtros plegables (toggle + active chips): panel desplegable a demanda,
 *    chips de filtros activos siempre visibles. Cierra por click fuera o aspa.
 *
 * @version: 20/05/2026_1 (filtros plegables)
 */
@Component({
  selector: 'app-proyectos-list',
  templateUrl: './proyectos-list.component.html',
  styleUrls: ['./proyectos-list.component.scss']
})
export class ProyectosListComponent implements OnInit, OnDestroy {

  // ---------- Datos del listado ----------
  proyectos: ProjectListItem[] = [];
  total: number = 0;
  totalPages: number = 0;

  // ---------- Estado paginación / filtros / orden ----------
  page: number = 1;
  pageSize: number = DEFAULT_PAGE_SIZE;
  q: string = '';
  societyId: string | null = null;
  typeId: string | null = null;
  systemId: string | null = null;
  sort: string = 'creationdate:desc';

  // ---------- Catálogos para dropdowns de filtros ----------
  societies: Society[] = [];
  types: Type[] = [];
  systems: System[] = [];

  // ---------- Estados UI ----------
  isInitialLoading: boolean = true;
  isReloading: boolean = false;
  showModal: boolean = false;
  filtersOpen: boolean = false;

  // ---------- Chips activos (derivado, recalculado al hidratar) ----------
  activeChips: ActiveFilterChip[] = [];

  // ---------- Opciones de orden (UI dropdown) ----------
  readonly sortOptions: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'creationdate:desc', label: 'Creación (más reciente)' },
    { value: 'creationdate:asc',  label: 'Creación (más antiguo)' },
    { value: 'name:asc',          label: 'Nombre (A-Z)' },
    { value: 'name:desc',         label: 'Nombre (Z-A)' }
  ];

  // ---------- Refs para click-outside del panel de filtros ----------
  @ViewChild('filtersPanel') filtersPanelRef?: ElementRef<HTMLElement>;
  @ViewChild('filtersBtn')   filtersBtnRef?:   ElementRef<HTMLElement>;

  // ---------- Streams ----------
  private readonly searchSubject$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private proyectoService: ProyectoService,
    private societyService: SocietyService,
    private typeService: TypeService,
    private systemService: SystemService,
    private authService: AuthService,
    private logger: LoggerService,
    private router: Router,
    private route: ActivatedRoute,
    private globalModalService: GlobalModalService
  ) {}

  ngOnInit(): void {
    sessionStorage.removeItem('selectedProject');

    this.loadCatalogs();

    this.searchSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((value: string) => {
        this.updateUrl({ q: value || null, page: 1 });
      });

    this.route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        tap((params: any) => {
          this.hydrateFromUrl(params);
          if (!this.isInitialLoading) this.isReloading = true;
        }),
        switchMap(() =>
          this.proyectoService.getProyectosPaginated(this.buildQuery()).pipe(
            catchError((err: any) => {
              this.logger.error('Error al obtener los proyectos', err?.error?.message);
              const empty: PagedResponse<ProjectListItem> = {
                items: [], total: 0, page: this.page, pageSize: this.pageSize, totalPages: 0
              };
              return of(empty);
            })
          )
        )
      )
      .subscribe((res: PagedResponse<ProjectListItem>) => {
        this.proyectos = res.items;
        this.total = res.total;
        this.totalPages = res.totalPages;
        this.isInitialLoading = false;
        this.isReloading = false;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ==========================================================================
  //  Click-outside para cerrar el panel de filtros
  // ==========================================================================

  /**
   * Si el panel está abierto y el click ocurre FUERA del panel y FUERA del
   * botón toggle, se cierra. Excluir el botón evita conflicto con su propio
   * (click)="toggleFilters()" — el toggle lo gestiona el botón.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.filtersOpen) return;
    const target = event.target as HTMLElement;
    const panelEl = this.filtersPanelRef?.nativeElement;
    const btnEl   = this.filtersBtnRef?.nativeElement;
    if (panelEl && btnEl && !panelEl.contains(target) && !btnEl.contains(target)) {
      this.filtersOpen = false;
    }
  }

  // ==========================================================================
  //  Carga inicial de catálogos
  // ==========================================================================

  private loadCatalogs(): void {
    // Tras cargar cada catálogo, refrescamos chips: si la URL entrante traía
    // un filtro por ID, hasta ahora mostrábamos placeholder; ahora resolvemos.
    this.societyService.getAllSocieties().subscribe({
      next: (data: Society[]) => { this.societies = data || []; this.updateActiveChips(); },
      error: (err: any)        => { this.logger.error('Error cargando sociedades', err); }
    });
    this.typeService.getAllTypes().subscribe({
      next: (data: Type[])     => { this.types = data || []; this.updateActiveChips(); },
      error: (err: any)        => { this.logger.error('Error cargando tipos', err); }
    });
    this.systemService.getAllSystems().subscribe({
      next: (data: System[])   => { this.systems = data || []; this.updateActiveChips(); },
      error: (err: any)        => { this.logger.error('Error cargando sistemas', err); }
    });
  }

  // ==========================================================================
  //  URL ⇄ estado local
  // ==========================================================================

  private hydrateFromUrl(params: any): void {
    this.page      = parseInt(params['page'], 10) || 1;
    this.pageSize  = parseInt(params['pageSize'], 10) || DEFAULT_PAGE_SIZE;
    this.q         = params['q'] || '';
    this.societyId = params['societyId'] || null;
    this.typeId    = params['typeId']    || null;
    this.systemId  = params['systemId']  || null;
    this.sort      = params['sort'] || 'creationdate:desc';
    this.updateActiveChips();
  }

  private buildQuery(): ProjectListQueryParams {
    const out: ProjectListQueryParams = {
      page: this.page,
      pageSize: this.pageSize,
      sort: this.sort
    };
    if (this.q)         out.q         = this.q;
    if (this.societyId) out.societyId = this.societyId;
    if (this.typeId)    out.typeId    = this.typeId;
    if (this.systemId)  out.systemId  = this.systemId;
    return out;
  }

  private updateUrl(partial: { [key: string]: any }): void {
    const normalized: { [key: string]: any } = {};
    for (const key of Object.keys(partial)) {
      const v = partial[key];
      normalized[key] = (v === '' || v === undefined) ? null : v;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: normalized,
      queryParamsHandling: 'merge'
    });
  }

  // ==========================================================================
  //  Filtros plegables: toggle, chips y remove individual
  // ==========================================================================

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  closeFilters(): void {
    this.filtersOpen = false;
  }

  /**
   * Quita un filtro individual desde su chip ×. No cierra el panel si está
   * abierto: el usuario podría seguir tocando filtros mientras retira uno.
   */
  removeFilter(key: FilterKey): void {
    this.updateUrl({ [key]: null, page: 1 });
  }

  /**
   * Recalcula `activeChips` desde el estado actual + catálogos cargados.
   * Si un catálogo aún no ha llegado, mostramos '…' como placeholder; al
   * cargar el catálogo se llama de nuevo y resuelve el nombre legible.
   */
  private updateActiveChips(): void {
    const chips: ActiveFilterChip[] = [];
    if (this.societyId) {
      const s = this.societies.find(x => x.id === Number(this.societyId));
      chips.push({ key: 'societyId', label: 'Sociedad: ' + (s?.name || '…') });
    }
    if (this.typeId) {
      const t = this.types.find(x => x.id === Number(this.typeId));
      chips.push({ key: 'typeId', label: 'Tipo: ' + (t?.name || '…') });
    }
    if (this.systemId) {
      const sys = this.systems.find(x => x.id === Number(this.systemId));
      chips.push({ key: 'systemId', label: 'Sistema: ' + (sys?.name || '…') });
    }
    this.activeChips = chips;
  }

  trackByChipKey(_: number, chip: ActiveFilterChip): string {
    return chip.key;
  }

  // ==========================================================================
  //  Handlers de UI
  // ==========================================================================

  onSearchInput(value: string): void {
    this.q = value;
    this.searchSubject$.next(value);
  }

  onSocietyChange(value: string): void { this.updateUrl({ societyId: value || null, page: 1 }); }
  onTypeChange(value: string): void    { this.updateUrl({ typeId:    value || null, page: 1 }); }
  onSystemChange(value: string): void  { this.updateUrl({ systemId:  value || null, page: 1 }); }
  onSortChange(value: string): void    { this.updateUrl({ sort:      value || null, page: 1 }); }

  onPageChange(page: number): void           { this.updateUrl({ page }); }
  onPageSizeChange(pageSize: number): void   { this.updateUrl({ pageSize, page: 1 }); }

  /**
   * Resetea filtros y búsqueda. Conserva pageSize y sort (preferencias de
   * presentación, no filtros). Navegación sin merge para borrar el resto.
   */
  clearFilters(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: 1,
        pageSize: this.pageSize,
        sort: this.sort
      }
    });
  }

  hasActiveFilters(): boolean {
    return !!(this.q || this.societyId || this.typeId || this.systemId);
  }

  // ==========================================================================
  //  Navegación al detalle
  // ==========================================================================

  loadProyecto(id: string): void {
    this.logger.info('ProyectosListComponent.loadProyecto');
    sessionStorage.setItem('selectedProject', JSON.stringify({ id }));
    this.router.navigate(['/app/proyecto']);
  }

  // ==========================================================================
  //  Helpers del template
  // ==========================================================================

  computePercent(item: ProjectListItem): number {
    if (!item.totalEstimatedHours || item.totalEstimatedHours <= 0) return 0;
    return Math.round((item.totalExecutedHours / item.totalEstimatedHours) * 100);
  }

  getProgressClass(item: ProjectListItem): string {
    if (!item.totalEstimatedHours) return 'is-unset';
    const pct = this.computePercent(item);
    if (pct > 110) return 'is-overrun';
    if (pct > 100) return 'is-warning';
    return 'is-ok';
  }

  fillWidth(item: ProjectListItem): number {
    const pct = this.computePercent(item);
    return Math.min(100, pct);
  }

  trackById(_: number, item: ProjectListItem): string {
    return item.id;
  }

  openModal(): void  { this.showModal = true; }
  closeModal(): void { this.showModal = false; }
}